import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  MagnifyingGlassIcon,
  EyeIcon,
  PencilIcon,
  FunnelIcon,
  ArrowPathIcon,
  CheckIcon,
  XMarkIcon,
  MapIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { Plot, PlotFilters, PlotStatus, Layout, PlotsResponse } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';
import StatusUpdateModal from '../../components/StatusUpdateModal';

const PlotsPage: React.FC = () => {
  const [plots, setPlots] = useState<Plot[]>([]);
  const [layouts, setLayouts] = useState<Layout[]>([]);
  const [plotStatuses, setPlotStatuses] = useState<PlotStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPlots, setSelectedPlots] = useState<number[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [bulkUpdateModal, setBulkUpdateModal] = useState<{ show: boolean; statusId?: number }>({
    show: false
  });
  const [statusModalPlot, setStatusModalPlot] = useState<Plot | null>(null);
  
  const [filters, setFilters] = useState<PlotFilters>({
    page: 1,
    limit: 20,
    search: '',
    layout_id: '',
    status_ids: '',
    facing_direction: '',
    corner_plot: '',
    price_min: '',
    price_max: '',
    size_min: '',
    size_max: ''
  });
  
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    total: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  const [searchTerm, setSearchTerm] = useState('');
  const { state: authState } = useAuth();

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadPlots();
  }, [filters]);

  const loadInitialData = async () => {
    try {
      const [layoutsRes, statusesRes] = await Promise.all([
        apiService.getLayouts({ limit: 100 }),
        apiService.getPlotStatuses()
      ]);
      
      setLayouts(layoutsRes.data.items);
      setPlotStatuses(statusesRes.data.statuses);
    } catch (err: any) {
      console.error('Failed to load initial data:', err);
    }
  };

  const loadPlots = async () => {
    try {
      setLoading(true);
      setError(null);
      const response: PlotsResponse = await apiService.getPlots(filters);
      setPlots(response.data.plots || []);
      setPagination({
        ...response.data.pagination,
        total: response.data.pagination.totalPlots || 0
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load plots');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters(prev => ({
      ...prev,
      search: searchTerm,
      page: 1
    }));
  };

  const handleFilterChange = (key: keyof PlotFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      page: 1
    }));
  };

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleSelectPlot = (plotId: number) => {
    setSelectedPlots(prev => 
      prev.includes(plotId) 
        ? prev.filter(id => id !== plotId)
        : [...prev, plotId]
    );
  };

  const handleSelectAll = () => {
    if (selectedPlots.length === plots.length) {
      setSelectedPlots([]);
    } else {
      setSelectedPlots(plots.map(plot => plot.id));
    }
  };

  const handleBulkStatusUpdate = async (statusId: number) => {
    if (selectedPlots.length === 0) return;

    try {
      await apiService.bulkUpdatePlots({
        plot_ids: selectedPlots,
        updates: { status_id: statusId },
        notes: `Bulk status update to ${plotStatuses.find(s => s.id === statusId)?.status_name}`
      });
      
      setSelectedPlots([]);
      setBulkUpdateModal({ show: false });
      loadPlots();
    } catch (err: any) {
      alert(err.message || 'Failed to update plots');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      page: 1,
      limit: 20,
      search: '',
      layout_id: '',
      status_ids: '',
      facing_direction: '',
      corner_plot: '',
      price_min: '',
      price_max: '',
      size_min: '',
      size_max: ''
    });
  };

  const formatPrice = (price: number) => {
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(1)}Cr`;
    } else if (price >= 100000) {
      return `₹${(price / 100000).toFixed(1)}L`;
    } else {
      return `₹${price.toLocaleString()}`;
    }
  };

  const getStatusBadge = (plot: Plot) => {
    const statusColors: { [key: string]: string } = {
      'Open': 'bg-green-100 text-green-800',
      'Hold': 'bg-yellow-100 text-yellow-800',
      'Reserved': 'bg-blue-100 text-blue-800',
      'Token': 'bg-purple-100 text-purple-800',
      'Agreement': 'bg-indigo-100 text-indigo-800',
      'Registered': 'bg-gray-100 text-gray-800'
    };
    
    const colorClass = statusColors[plot.status_name || ''] || 'bg-gray-100 text-gray-800';
    
    return (
      <button
        type="button"
        onClick={() => setStatusModalPlot(plot)}
        className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium focus:outline-none ${colorClass}`}
        title="Update Status"
      >
        <span>{plot.status_name}</span>
        <ChevronDownIcon className="h-3 w-3" />
      </button>
    );
  };

  const isAdmin = authState.user?.role === 'Admin';

  if (loading && plots.length === 0) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="card p-6">
            <div className="space-y-4">
              {Array.from({ length: 10 }).map((_, i) => (
                <div key={i} className="flex space-x-4">
                  <div className="h-4 bg-gray-200 rounded flex-1"></div>
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Plots</h1>
          <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">Manage your plot inventory</p>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-outline flex-1 sm:flex-initial"
          >
            <FunnelIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            <span className="text-sm sm:text-base">Filters</span>
          </button>
          <button onClick={loadPlots} className="btn-outline flex-1 sm:flex-initial">
            <ArrowPathIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            <span className="text-sm sm:text-base hidden sm:inline">Refresh</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="card p-4 sm:p-6">
        <form onSubmit={handleSearch} className="flex items-center space-x-2 sm:space-x-4">
          <div className="flex-1 relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              className="form-input pl-10"
              placeholder="Search by plot number, customer name, layout..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button type="submit" className="btn-primary">
            Search
          </button>
          {(filters.search || Object.values(filters).some(v => v && v !== '1' && v !== '20')) && (
            <button type="button" onClick={clearFilters} className="btn-outline">
              Clear All
            </button>
          )}
        </form>
      </div>

      {/* Advanced Filters */}
      {showFilters && (
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Advanced Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="form-label">Layout</label>
              <select
                className="form-input"
                value={filters.layout_id || ''}
                onChange={(e) => handleFilterChange('layout_id', e.target.value)}
              >
                <option value="">All Layouts</option>
                {layouts.map(layout => (
                  <option key={layout.id} value={layout.id}>{layout.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Status</label>
              <select
                className="form-input"
                value={filters.status_ids || ''}
                onChange={(e) => handleFilterChange('status_ids', e.target.value)}
              >
                <option value="">All Statuses</option>
                {plotStatuses.map(status => (
                  <option key={status.id} value={status.id}>{status.status_name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Facing Direction</label>
              <select
                className="form-input"
                value={filters.facing_direction || ''}
                onChange={(e) => handleFilterChange('facing_direction', e.target.value)}
              >
                <option value="">All Directions</option>
                <option value="North">North</option>
                <option value="South">South</option>
                <option value="East">East</option>
                <option value="West">West</option>
                <option value="North-East">North-East</option>
                <option value="North-West">North-West</option>
                <option value="South-East">South-East</option>
                <option value="South-West">South-West</option>
              </select>
            </div>

            <div>
              <label className="form-label">Corner Plot</label>
              <select
                className="form-input"
                value={filters.corner_plot || ''}
                onChange={(e) => handleFilterChange('corner_plot', e.target.value)}
              >
                <option value="">All Plots</option>
                <option value="true">Corner Plots Only</option>
                <option value="false">Non-Corner Plots</option>
              </select>
            </div>

            <div>
              <label className="form-label">Min Price (₹)</label>
              <input
                type="number"
                className="form-input"
                placeholder="Minimum price"
                value={filters.price_min || ''}
                onChange={(e) => handleFilterChange('price_min', e.target.value)}
              />
            </div>

            <div>
              <label className="form-label">Max Price (₹)</label>
              <input
                type="number"
                className="form-input"
                placeholder="Maximum price"
                value={filters.price_max || ''}
                onChange={(e) => handleFilterChange('price_max', e.target.value)}
              />
            </div>

            <div>
              <label className="form-label">Min Size (sq ft)</label>
              <input
                type="number"
                className="form-input"
                placeholder="Minimum size"
                value={filters.size_min || ''}
                onChange={(e) => handleFilterChange('size_min', e.target.value)}
              />
            </div>

            <div>
              <label className="form-label">Max Size (sq ft)</label>
              <input
                type="number"
                className="form-input"
                placeholder="Maximum size"
                value={filters.size_max || ''}
                onChange={(e) => handleFilterChange('size_max', e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="card p-6">
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={loadPlots} className="btn-primary">
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Mobile Card List */}
      <div className="md:hidden space-y-3">
        {plots.length === 0 ? (
          <div className="card p-6 text-center">
            <MapIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-sm text-gray-500">
              {Object.values(filters).some(v => v && v !== '1' && v !== '20')
                ? 'No plots match your filters'
                : 'No plots available'
              }
            </p>
          </div>
        ) : (
          plots.map((plot) => (
            <div key={plot.id} className="card p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-start space-x-3 flex-1 min-w-0">
                  <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <MapIcon className="h-5 w-5 text-primary-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-gray-900">Plot {plot.plot_number}</h3>
                    <p className="text-xs text-gray-500 truncate">{plot.layout_name}</p>
                    <p className="text-xs text-gray-500">{plot.facing_direction} facing{plot.corner_plot && ' • Corner'}</p>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {getStatusBadge(plot)}
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                <div>
                  <span className="text-gray-500">Size:</span>
                  <span className="ml-1 font-medium text-gray-900">{plot.size_sqft} sqft</span>
                </div>
                <div>
                  <span className="text-gray-500">Price:</span>
                  <span className="ml-1 font-medium text-gray-900">{formatPrice(plot.price)}</span>
                </div>
                {plot.customer_name && (
                  <>
                    <div className="col-span-2 truncate">
                      <span className="text-gray-500">Customer:</span>
                      <span className="ml-1 text-gray-900">{plot.customer_name}</span>
                    </div>
                  </>
                )}
              </div>
              
              <div className="flex gap-2">
                <Link
                  to={`/plots/${plot.id}`}
                  className="btn-outline flex-1 text-center py-2 text-sm"
                >
                  <EyeIcon className="h-4 w-4 inline mr-1" />
                  Details
                </Link>
                <button
                  onClick={() => setStatusModalPlot(plot)}
                  className="btn-primary flex-1 py-2 text-sm"
                >
                  <PencilIcon className="h-4 w-4 inline mr-1" />
                  Update
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop Table */}
      <div className="card overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plot Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Layout
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Size & Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {plots.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <MapIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">
                      {Object.values(filters).some(v => v && v !== '1' && v !== '20')
                        ? 'No plots found matching your criteria'
                        : 'No plots available'
                      }
                    </p>
                  </td>
                </tr>
              ) : (
                plots.map((plot) => (
                  <tr key={plot.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-primary-100 rounded-lg flex items-center justify-center">
                          <MapIcon className="h-5 w-5 text-primary-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            Plot {plot.plot_number}
                          </div>
                          <div className="text-sm text-gray-500">
                            {plot.facing_direction} facing
                            {plot.corner_plot && (
                              <span className="ml-2 px-2 py-0.5 bg-yellow-100 text-yellow-800 text-xs rounded">
                                Corner
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{plot.layout_name}</div>
                      <div className="text-sm text-gray-500">{plot.layout_location}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatPrice(plot.price)}
                      </div>
                      <div className="text-sm text-gray-500">
                        {plot.size_sqft} sq ft ({plot.size_sqm} sq m)
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(plot)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {plot.customer_name ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {plot.customer_name}
                          </div>
                          {plot.customer_phone && (
                            <div className="text-sm text-gray-500">
                              {plot.customer_phone}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-sm text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/plots/${plot.id}`}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="View Plot"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </Link>
                        {isAdmin && (
                          <button
                            onClick={() => {/* Handle edit */}}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit Plot"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination - Appears after both mobile and desktop views */}
      {plots.length > 0 && (
        <div className="card px-4 py-3 flex items-center justify-between">
          <div className="flex-1 flex justify-between md:hidden">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-xs text-gray-600 flex items-center">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={!pagination.hasNextPage}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden md:flex-1 md:flex md:items-center md:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{' '}
                <span className="font-medium">
                  {(pagination.currentPage - 1) * filters.limit! + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium">
                  {Math.min(pagination.currentPage * filters.limit!, pagination.total)}
                </span>{' '}
                of <span className="font-medium">{pagination.total}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={!pagination.hasPrevPage}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                
                {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                  const page = i + 1;
                  const isActive = page === pagination.currentPage;
                  
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        isActive
                          ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={!pagination.hasNextPage}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {loading && plots.length > 0 && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg">
            <div className="flex items-center">
              <div className="loading-spinner mr-3"></div>
              <p>Loading plots...</p>
            </div>
          </div>
        </div>
      )}

      {statusModalPlot && (
        <StatusUpdateModal
          plot={statusModalPlot}
          statuses={plotStatuses}
          onClose={() => setStatusModalPlot(null)}
          onUpdated={(updated) => {
            setPlots(plots.map(p => p.id === updated.id ? { ...p, ...updated } : p));
          }}
        />
      )}
    </div>
  );
};

export default PlotsPage; 